import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SelectedFilter } from '~constants/enums';

import { FilterCategory, rankFilterCategories } from './filterCategorySearch.utils';
import { FILTER_MENU_ITEM_HEIGHT_IN_PX } from './FilterMenuItem';
import SearchField from './SearchField';

const VISIBLE_CATEGORY_COUNT = 10;

interface FilterCategorySearchProps {
  activeFilters: Set<SelectedFilter>;
  categories: FilterCategory[];
  focusSearchFieldOnMount?: boolean;
  onRequestClose?: () => void;
  onSearchValueChange: (value: string) => void;
  onSelectCategory: (filter: SelectedFilter) => void;
  searchValue: string;
}

export default function FilterCategorySearch({
  activeFilters,
  categories,
  focusSearchFieldOnMount,
  onRequestClose,
  onSearchValueChange,
  onSelectCategory,
  searchValue,
}: FilterCategorySearchProps) {
  const theme = useTheme();
  const [highlightIndex, setHighlightIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  const rankedCategories = useMemo(
    () => rankFilterCategories(categories, searchValue),
    [categories, searchValue],
  );

  useEffect(() => {
    setHighlightIndex(0);
  }, [searchValue]);

  useEffect(() => {
    itemRefs.current[highlightIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightIndex]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Escape dismisses the whole menu (the search field otherwise stops the key from
      // reaching the MUI Menu's own Escape-to-close handling).
      if (event.key === 'Escape') {
        if (onRequestClose) {
          event.preventDefault();
          onRequestClose();
        }

        return;
      }

      if (rankedCategories.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightIndex((index) => (index + 1) % rankedCategories.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightIndex(
          (index) => (index - 1 + rankedCategories.length) % rankedCategories.length,
        );
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const highlightedCategory =
          rankedCategories[highlightIndex] ?? rankedCategories[0];

        if (highlightedCategory) {
          onSelectCategory(highlightedCategory.filter);
        }
      }
    },
    [rankedCategories, highlightIndex, onSelectCategory, onRequestClose],
  );

  return (
    <>
      <SearchField
        onChange={onSearchValueChange}
        onKeyDown={handleKeyDown}
        placeholder={t('dispatch.dispatch_v2.filters.search_filters')}
        setFocusOnMount={focusSearchFieldOnMount}
        value={searchValue}
      />
      <Divider sx={{ '&.MuiDivider-root': { mt: 0, mb: 1, mx: 2 } }} />

      {rankedCategories.length === 0 ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography color={theme.brandV2.colors.treadGray3} variant="body2">
            {t('dispatch.dispatch_v2.filters.no_matching_filters')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: FILTER_MENU_ITEM_HEIGHT_IN_PX * VISIBLE_CATEGORY_COUNT,
            overflow: 'auto',
          }}
        >
          {rankedCategories.map((category, index) => (
            <MenuItem
              key={category.filter}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              selected={index === highlightIndex}
              onClick={() => onSelectCategory(category.filter)}
              onMouseEnter={() => setHighlightIndex(index)}
              data-ui="page-dispatch__orders-dispatch-filters__button"
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                  {category.label}
                </Typography>

                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  {activeFilters.has(category.filter) && (
                    <Check
                      sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Box>
      )}
    </>
  );
}
